// Stock materiali per furgone tecnico
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Truck, Plus, Package, Warehouse as WarehouseIcon } from 'lucide-react'
import type { VanStockItem } from '@fieldcore/types'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { CardSkeleton } from '../../components/ui/Skeleton'
import { useAuth } from '../../hooks/useAuth'
import api from '../../lib/api'

interface WarehouseOption {
  id: string
  name: string
  type: 'central' | 'peripheral'
  city?: string
}

interface VanStock {
  id: string
  technicianId: string
  technicianName: string
  warehouseId?: string
  warehouse?: WarehouseOption
  items: VanStockItem[]
}

interface Technician {
  id: string
  firstName: string
  lastName: string
}

interface TechVan {
  tech: Technician
  van: VanStock | null
}

interface Material {
  id: string
  code: string
  name: string
  unit: string
  category?: string
}

// Modal per aggiungere materiali al furgone di un tecnico
function AddMaterialModal({
  tech,
  fromWarehouseId: initialWarehouseId,
  onClose,
}: {
  tech: Technician
  fromWarehouseId?: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [materialId, setMaterialId] = useState('')
  const [materialSearch, setMaterialSearch] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null)
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>(initialWarehouseId ?? '')

  const { data: warehouses = [] } = useQuery<WarehouseOption[]>({
    queryKey: ['warehouses'],
    queryFn: () => api.get('/warehouse/locations').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    onSuccess: (data: WarehouseOption[]) => {
      // Se non c'è warehouse pre-selezionato, usa il primo disponibile
      if (!selectedWarehouseId && data.length > 0) {
        setSelectedWarehouseId(data[0].id)
      }
    },
  } as any)

  const { data: materials = [], isLoading: matLoading } = useQuery({
    queryKey: ['warehouse', 'materials', selectedWarehouseId],
    queryFn: async () => {
      const res = await api.get<Material[]>('/warehouse/materials', {
        params: selectedWarehouseId ? { warehouseId: selectedWarehouseId } : undefined,
      })
      return Array.isArray(res.data) ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!selectedWarehouseId,
  })

  const filtered = materials.filter(
    (m) =>
      !materialSearch ||
      m.name.toLowerCase().includes(materialSearch.toLowerCase()) ||
      m.code.toLowerCase().includes(materialSearch.toLowerCase()),
  )

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/warehouse/movements', {
        operationType: 'outbound_technician',
        materialId,
        quantity,
        toTechnicianId: tech.id,
        fromWarehouseId: selectedWarehouseId || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['van-stocks'] })
      onClose()
    },
  })

  function handleSelectMaterial(m: Material) {
    setSelectedMaterial(m)
    setMaterialId(m.id)
    setMaterialSearch(m.name)
  }

  const showDropdown = materialSearch.length > 0 && !selectedMaterial

  return (
    <Modal
      open
      onClose={onClose}
      title={`Carica materiali — ${tech.firstName} ${tech.lastName}`}
      size="md"
    >
      <div className="space-y-4">
        {/* Selettore magazzino sorgente */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">Carica da magazzino</label>
          <select
            value={selectedWarehouseId}
            onChange={(e) => { setSelectedWarehouseId(e.target.value); setSelectedMaterial(null); setMaterialId(''); setMaterialSearch('') }}
            className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:border-[var(--fc-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-border-focus)]/20"
          >
            <option value="">— Seleziona magazzino —</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.type === 'central' ? '🏭' : '📦'} {w.name}
              </option>
            ))}
          </select>
        </div>

        {/* Ricerca materiale */}
        <div className="space-y-1 relative">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">
            Materiale
          </label>
          <input
            type="text"
            placeholder="Cerca materiale per nome o codice..."
            value={materialSearch}
            onChange={(e) => {
              setMaterialSearch(e.target.value)
              if (selectedMaterial && e.target.value !== selectedMaterial.name) {
                setSelectedMaterial(null)
                setMaterialId('')
              }
            }}
            className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-border-focus)]/20"
          />
          {/* Dropdown suggerimenti */}
          {showDropdown && !matLoading && filtered.length > 0 && (
            <div className="absolute z-10 w-full mt-1 max-h-52 overflow-y-auto rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-lg">
              {filtered.slice(0, 20).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleSelectMaterial(m)}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--fc-surface-overlay)] flex items-center justify-between"
                >
                  <span className="text-sm text-[var(--fc-foreground)]">{m.name}</span>
                  <span className="text-xs font-mono text-[var(--fc-foreground-muted)]">{m.code} · {m.unit}</span>
                </button>
              ))}
            </div>
          )}
          {showDropdown && !matLoading && filtered.length === 0 && (
            <div className="absolute z-10 w-full mt-1 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-lg px-3 py-4 text-sm text-[var(--fc-foreground-muted)] text-center">
              Nessun materiale trovato
            </div>
          )}
        </div>

        {/* Materiale selezionato */}
        {selectedMaterial && (
          <div className="rounded-lg border border-[var(--fc-success)] bg-[color-mix(in_srgb,var(--fc-success)_8%,transparent)] px-4 py-3 flex items-center gap-3">
            <Package className="h-4 w-4 text-[var(--fc-success)] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[var(--fc-foreground)] truncate">{selectedMaterial.name}</p>
              <p className="text-xs text-[var(--fc-foreground-muted)]">{selectedMaterial.code} · {selectedMaterial.unit}</p>
            </div>
          </div>
        )}

        {/* Quantità */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">
            Quantità {selectedMaterial ? `(${selectedMaterial.unit})` : ''}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="h-10 w-10 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] text-xl font-light hover:bg-[var(--fc-surface-overlay)] transition-colors"
            >
              −
            </button>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-24 text-center rounded-lg border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-lg font-bold text-[var(--fc-foreground)] focus:border-[var(--fc-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-border-focus)]/20"
            />
            <button
              type="button"
              onClick={() => setQuantity((q) => q + 1)}
              className="h-10 w-10 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] text-xl font-light hover:bg-[var(--fc-surface-overlay)] transition-colors"
            >
              +
            </button>
          </div>
        </div>

        {/* Azioni */}
        <div className="flex gap-3 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            disabled={!materialId || quantity < 1 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Caricamento...' : 'Conferma carico'}
          </Button>
        </div>

        {mutation.isError && (
          <p className="text-sm text-[var(--fc-danger)] text-center">
            Errore durante il carico. Verifica la disponibilità a magazzino.
          </p>
        )}
      </div>
    </Modal>
  )
}

// Selettore inline magazzino di zona per un furgone
function WarehouseAssignSelect({
  technicianId,
  currentWarehouseId,
}: {
  technicianId: string
  currentWarehouseId?: string
}) {
  const queryClient = useQueryClient()

  const { data: warehouses = [] } = useQuery<WarehouseOption[]>({
    queryKey: ['warehouses'],
    queryFn: () => api.get('/warehouse/locations').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (warehouseId: string) =>
      api.patch(`/warehouse/van/${technicianId}/warehouse`, { warehouseId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['van-stocks'] }),
  })

  return (
    <div className="flex items-center gap-2 mt-2">
      <WarehouseIcon className="h-3.5 w-3.5 text-[var(--fc-foreground-muted)] shrink-0" />
      <select
        value={currentWarehouseId ?? ''}
        disabled={mutation.isPending || warehouses.length === 0}
        onChange={(e) => mutation.mutate(e.target.value)}
        className="flex-1 min-w-0 rounded border border-[var(--fc-border)] bg-[var(--fc-background)] px-2 py-1 text-xs text-[var(--fc-foreground)] focus:border-[var(--fc-border-focus)] focus:outline-none disabled:opacity-60"
      >
        {warehouses.length === 0 && <option value="">Caricamento...</option>}
        {warehouses.map((w) => (
          <option key={w.id} value={w.id}>
            {w.type === 'central' ? '🏭' : '📦'} {w.name}
          </option>
        ))}
      </select>
      {mutation.isPending && (
        <span className="text-xs text-[var(--fc-foreground-muted)]">...</span>
      )}
      {mutation.isError && (
        <span className="text-xs text-[var(--fc-danger)]">Errore</span>
      )}
    </div>
  )
}

export function VanStocksPage() {
  const [addForTech, setAddForTech] = useState<Technician | null>(null)
  const [addFromWarehouseId, setAddFromWarehouseId] = useState<string | undefined>(undefined)
  const { user } = useAuth()
  const isTechnician = user?.role === 'technician'

  const { data: techVans = [], isLoading } = useQuery({
    queryKey: ['van-stocks', user?.id],
    queryFn: async () => {
      if (isTechnician && user?.id) {
        // Tecnico: vede solo il proprio furgone
        try {
          const vanRes = await api.get<VanStock>(`/warehouse/van/${user.id}`)
          const tech: Technician = { id: user.id, firstName: user.firstName ?? '', lastName: user.lastName ?? '' }
          return [{ tech, van: vanRes.data }] as TechVan[]
        } catch {
          const tech: Technician = { id: user.id, firstName: user.firstName ?? '', lastName: user.lastName ?? '' }
          return [{ tech, van: null }] as TechVan[]
        }
      }

      // Admin/team_leader: vede tutti i tecnici del gruppo
      const techRes = await api.get<Technician[]>('/technicians')
      const techs = Array.isArray(techRes.data) ? techRes.data : []

      const results: TechVan[] = await Promise.all(
        techs.slice(0, 20).map(async (t) => {
          try {
            const vanRes = await api.get<VanStock>(`/warehouse/van/${t.id}`)
            return {
              tech: t,
              van: {
                ...vanRes.data,
                technicianName: vanRes.data.technicianName ?? `${t.firstName} ${t.lastName}`,
              },
            }
          } catch {
            return { tech: t, van: null }
          }
        }),
      )
      return results
    },
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Stock furgoni"
        breadcrumb={[{ label: 'Magazzino', href: '/warehouse' }, { label: 'Stock furgoni' }]}
      />

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[1, 2, 3].map((i) => <CardSkeleton key={i} />)}
        </div>
      ) : techVans.length === 0 ? (
        <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-12 text-center">
          <Truck className="mx-auto h-12 w-12 text-[var(--fc-foreground-muted)] opacity-40" />
          <p className="mt-3 text-sm text-[var(--fc-foreground-muted)]">Nessun tecnico registrato</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {techVans.map(({ tech, van }) => {
            const items = van?.items ?? []
            const lowItems = items.filter((i) => i.quantity <= i.minQuantity)
            return (
              <Card key={tech.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--fc-warning)_15%,transparent)] shrink-0">
                    <Truck className="h-5 w-5 text-[var(--fc-warning)]" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[var(--fc-foreground)] truncate">
                      {tech.firstName} {tech.lastName}
                    </p>
                    <p className="text-xs text-[var(--fc-foreground-muted)]">
                      {items.length > 0 ? `${items.length} articoli` : 'Nessun materiale caricato'}
                      {lowItems.length > 0 && (
                        <span className="ml-2 text-[var(--fc-warning)] font-semibold">
                          · {lowItems.length} sotto scorta
                        </span>
                      )}
                    </p>
                    {!isTechnician && (
                      <WarehouseAssignSelect
                        technicianId={tech.id}
                        currentWarehouseId={van?.warehouseId}
                      />
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() => { setAddForTech(tech); setAddFromWarehouseId(van?.warehouseId) }}
                  >
                    Aggiungi
                  </Button>
                </div>

                {items.length > 0 ? (
                  <ul className="space-y-2" role="list">
                    {items.slice(0, 5).map((item) => (
                      <li key={item.id} className="flex items-center justify-between text-sm">
                        <span className="text-[var(--fc-foreground-muted)] truncate flex-1">
                          {(item as VanStockItem & { material?: { name: string; code: string } }).material?.name
                            ?? item.materialId.slice(0, 8)}
                        </span>
                        <Badge variant={item.quantity <= item.minQuantity ? 'warning' : 'default'}>
                          {item.quantity}
                        </Badge>
                      </li>
                    ))}
                    {items.length > 5 && (
                      <p className="text-xs text-[var(--fc-foreground-muted)]">
                        +{items.length - 5} altri articoli
                      </p>
                    )}
                  </ul>
                ) : (
                  <div className="rounded-lg border border-dashed border-[var(--fc-border)] py-6 text-center">
                    <Package className="mx-auto h-8 w-8 text-[var(--fc-foreground-muted)] opacity-30 mb-2" />
                    <p className="text-xs text-[var(--fc-foreground-muted)]">
                      Nessun materiale nel furgone
                    </p>
                    <button
                      type="button"
                      onClick={() => { setAddForTech(tech); setAddFromWarehouseId(van?.warehouseId) }}
                      className="mt-2 text-xs text-[var(--fc-primary)] hover:underline"
                    >
                      Carica il primo materiale →
                    </button>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {addForTech && (
        <AddMaterialModal
          tech={addForTech}
          fromWarehouseId={addFromWarehouseId}
          onClose={() => { setAddForTech(null); setAddFromWarehouseId(undefined) }}
        />
      )}
    </div>
  )
}
